import type { ChannelStream, Device, LiveGpsUpdate, Position, StreamInfo } from "../api";
import { GPS_FRESH_MS, HEARTBEAT_ONLINE_MS, gpsFreshnessAt, isFreshTimestamp } from "./freshness";

export type CameraCardStatus = "offline" | "waiting" | "connecting" | "live" | "failed";
export type GpsUiStatus = "live" | "offline" | "reconnecting" | "last_known";

export function heartbeatOnline(lastHeartbeatAt: string | null | undefined, now = Date.now()): boolean {
  return isFreshTimestamp(lastHeartbeatAt, HEARTBEAT_ONLINE_MS, now);
}

export function gpsIsFresh(recordedAt: string | null | undefined, now = Date.now()): boolean {
  return isFreshTimestamp(recordedAt, GPS_FRESH_MS, now);
}

/** JT808 status bit 1 — positioned. Missing bits from older rows are not treated as a fix. */
export function statusHasGpsFix(statusBits: number | null | undefined): boolean {
  if (statusBits == null || !Number.isFinite(statusBits)) return false;
  return (statusBits & 0x2) !== 0;
}

/** What the latest 0x0200 actually said. Stale time is not an invalid fix. */
export type GpsFixReport = "fix" | "invalid" | "unknown";

export function gpsFixReport(
  gpsFix: boolean | undefined,
  statusBits: number | null | undefined,
): GpsFixReport {
  if (gpsFix === false) return "invalid";
  if (gpsFix === true) return "fix";
  if (statusBits == null || !Number.isFinite(statusBits)) return "unknown";
  return (statusBits & 0x2) !== 0 ? "fix" : "invalid";
}

/** 0x0200 BCD, stored as UTC. This is the camera OSD clock. Receipt time is not. */
function deviceClockIso(recordedAt: string | null | undefined): string | null {
  if (!recordedAt) return null;
  return Number.isFinite(Date.parse(recordedAt)) ? recordedAt : null;
}

export function gpsPoint(device: Device, live?: LiveGpsUpdate | null): {
  lat: number;
  lon: number;
  /** Device clock (0x0200). Format this for the card, map, and trip labels. */
  recordedAt: string | null;
  /** Server receipt. Live / last-known and "Xm ago" use this, not the device clock. */
  freshnessAt: string | null;
  liveFix: boolean;
  fixReport: GpsFixReport;
} | null {
  if (live) {
    const freshnessAt = gpsFreshnessAt(live.received_at, live.recorded_at);
    const fixReport = gpsFixReport(live.gps_fix, live.status_bits);
    return {
      lat: live.lat,
      lon: live.lon,
      recordedAt: deviceClockIso(live.recorded_at) ?? freshnessAt,
      freshnessAt,
      liveFix: fixReport === "fix" && gpsIsFresh(freshnessAt),
      fixReport,
    };
  }
  const p: Position | null = device.last_position;
  if (!p) return null;
  const freshnessAt = gpsFreshnessAt(p.received_at, p.recorded_at);
  const fixReport = gpsFixReport(undefined, p.status_bits);
  return {
    lat: p.lat,
    lon: p.lon,
    recordedAt: deviceClockIso(p.recorded_at) ?? freshnessAt,
    freshnessAt,
    liveFix: fixReport === "fix" && gpsIsFresh(freshnessAt),
    fixReport,
  };
}

export function dualCameraTiles(device: Device, info: StreamInfo | undefined): ChannelStream[] {
  const byNo = new Map((info?.channels ?? []).map((c) => [c.channel_no, c]));
  const placeholder = (channel_no: number): ChannelStream => {
    const mediamtx_path = `devices/${device.serial_no}/ch${channel_no}`;
    return {
      channel_no,
      mediamtx_path,
      // cookieCheck=1 → MediaMTX returns main_stream?session= (no Secure cookie).
      hlsUrl: `/hls/${mediamtx_path}/index.m3u8?cookieCheck=1`,
      webrtcUrl: `/webrtc/${mediamtx_path}`,
      has_audio: false,
      live: false,
      // Never mark online terminals as "waiting" — players show Connecting instead.
      waiting: false,
    };
  };
  return [byNo.get(1) ?? placeholder(1), byNo.get(2) ?? placeholder(2)].map((ch) => {
    if (!ch.hlsUrl) return ch;
    if (/[?&]cookieCheck=/i.test(ch.hlsUrl)) return ch;
    return {
      ...ch,
      hlsUrl: `${ch.hlsUrl}${ch.hlsUrl.includes("?") ? "&" : "?"}cookieCheck=1`,
    };
  });
}

export function cameraCardStatus(args: {
  deviceOnline: boolean;
  liveChannelCount: number;
  anyPlayerLive: boolean;
  anyPlayerConnecting: boolean;
  anyChannelFailed?: boolean;
}): CameraCardStatus {
  // A playing picture is live even when the last GPS report has no fix.
  if (args.anyPlayerLive || args.liveChannelCount > 0) return "live";
  if (!args.deviceOnline) return "offline";
  // Every start that was attempted has failed. Do not leave the chip on STARTING.
  if (args.anyChannelFailed && !args.anyPlayerConnecting) return "failed";
  return "connecting";
}

export function gpsUiStatus(args: {
  deviceOnline: boolean;
  socketConnected: boolean;
  recordedAt: string | null | undefined;
  liveFix?: boolean;
  /** 0x0200 status bit. Video state must not be passed here. */
  fixReport?: GpsFixReport;
}): GpsUiStatus {
  if (!args.deviceOnline) return args.recordedAt ? "last_known" : "offline";
  // NO GPS FIX only when the terminal reported that it is not positioned.
  if (args.fixReport === "invalid") return "offline";
  if ((args.fixReport === "fix" || args.liveFix) && gpsIsFresh(args.recordedAt)) return "live";
  if (args.fixReport === "fix" || args.liveFix) return "last_known";
  if (!args.socketConnected) return "reconnecting";
  return args.recordedAt ? "last_known" : "offline";
}
